import { useState, useEffect, useCallback } from "react";
import {
  getRecentChanges,
  getRecordHistory,
  getMyActivity,
  getUserActivity,
  getAuditStats,
  getTransaction,
  TransactionSummary,
  RecordHistoryResponse,
  UserActivityResponse,
  AuditStatsResponse,
  TransactionDetails,
} from "@service/auditService";
import { Clock, FileText, History, Activity } from "lucide-react";

export default function AuditView() {
  const [activeTab, setActiveTab] = useState<
    "recent" | "record" | "transaction" | "activity"
  >("recent");

  // Recent Changes State
  const [recentChanges, setRecentChanges] = useState<TransactionSummary[]>([]);
  const [recentLimit, setRecentLimit] = useState(20);
  const [recentModel, setRecentModel] = useState("");
  const [recentUserId, setRecentUserId] = useState("");
  const [recentUserType, setRecentUserType] = useState("");
  const [recentLoading, setRecentLoading] = useState(false);

  // Record History State
  const [recordModel, setRecordModel] = useState("PTORequest");
  const [recordId, setRecordId] = useState("");
  const [recordHistory, setRecordHistory] =
    useState<RecordHistoryResponse | null>(null);
  const [recordLoading, setRecordLoading] = useState(false);

  // Transaction Details State
  const [transactionId, setTransactionId] = useState("");
  const [transactionDetails, setTransactionDetails] = useState<
    TransactionDetails | TransactionSummary | null
  >(null);
  const [transactionDetailed, setTransactionDetailed] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(false);

  // Activity State
  const [myActivity, setMyActivity] = useState<UserActivityResponse | null>(
    null
  );
  const [activityDays, setActivityDays] = useState(30);
  const [activityUserId, setActivityUserId] = useState("");
  const [activityUserType, setActivityUserType] = useState("");
  const [activityLoading, setActivityLoading] = useState(false);

  // Stats State
  const [stats, setStats] = useState<AuditStatsResponse | null>(null);

  const loadRecentChanges = useCallback(async () => {
    setRecentLoading(true);
    try {
      const data = await getRecentChanges({
        limit: recentLimit,
        model: recentModel || undefined,
        user_id: recentUserId ? parseInt(recentUserId) : undefined,
        user_type: recentUserType || undefined,
      });
      setRecentChanges(data);
    } catch (error) {
      console.error("Error loading recent changes:", error);
    } finally {
      setRecentLoading(false);
    }
  }, [recentLimit, recentModel, recentUserId, recentUserType]);

  const loadRecordHistory = async () => {
    if (!recordId) return;
    setRecordLoading(true);
    try {
      const data = await getRecordHistory(recordModel, parseInt(recordId));
      setRecordHistory(data);
    } catch (error) {
      console.error("Error loading record history:", error);
    } finally {
      setRecordLoading(false);
    }
  };

  const loadTransactionDetails = async () => {
    if (!transactionId) return;
    setTransactionLoading(true);
    try {
      const data = await getTransaction(
        parseInt(transactionId),
        transactionDetailed
      );
      setTransactionDetails(data);
    } catch (error) {
      console.error("Error loading transaction details:", error);
    } finally {
      setTransactionLoading(false);
    }
  };

  const loadMyActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      let data;
      if (activityUserId) {
        data = await getUserActivity(
          parseInt(activityUserId),
          activityDays,
          activityUserType || undefined
        );
      } else {
        data = await getMyActivity(activityDays, activityUserType || undefined);
      }
      setMyActivity(data);
    } catch (error) {
      console.error("Error loading activity:", error);
    } finally {
      setActivityLoading(false);
    }
  }, [activityDays, activityUserId, activityUserType]);

  const loadStats = useCallback(async () => {
    try {
      const data = await getAuditStats(30);
      setStats(data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  }, []);

  const handleTransactionIdClick = async (transactionIdValue: number) => {
    setTransactionId(transactionIdValue.toString());
    setActiveTab("transaction");
    // Load the transaction details
    try {
      setTransactionLoading(true);
      const data = await getTransaction(
        transactionIdValue,
        transactionDetailed
      );
      setTransactionDetails(data);
    } catch (error) {
      console.error("Error loading transaction details:", error);
    } finally {
      setTransactionLoading(false);
    }
  };

  const handleRecordIdClick = async (model: string, id: number) => {
    setRecordModel(model);
    setRecordId(id.toString());
    setActiveTab("record");
    // Load the record history
    try {
      setRecordLoading(true);
      const data = await getRecordHistory(model, id);
      setRecordHistory(data);
    } catch (error) {
      console.error("Error loading record history:", error);
    } finally {
      setRecordLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadRecentChanges();
    loadMyActivity();
    loadStats();
  }, [loadRecentChanges, loadMyActivity, loadStats]);

  const renderRecentChanges = () => (
    <div className="tw-space-y-4">
      <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow">
        <h2 className="tw-text-2xl tw-font-bold tw-mb-4">Recent Changes</h2>

        {/* Filters */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-5 tw-gap-4 tw-mb-6">
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              User ID
            </label>
            <input
              type="number"
              value={recentUserId}
              onChange={(e) => setRecentUserId(e.target.value)}
              placeholder="All Users"
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
            />
          </div>

          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              User Type
            </label>
            <select
              value={recentUserType}
              onChange={(e) => setRecentUserType(e.target.value)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
            >
              <option value="">All Types</option>
              <option value="admin">admin</option>
              <option value="user">user</option>
            </select>
          </div>

          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              Model Filter
            </label>
            <select
              value={recentModel}
              onChange={(e) => setRecentModel(e.target.value)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
            >
              <option value="">All Models</option>
              <option value="PTORequest">PTO Request</option>
              <option value="Log">Log</option>
              <option value="Worker">Worker</option>
              <option value="Plant">Plant</option>
            </select>
          </div>

          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              Limit
            </label>
            <input
              type="number"
              value={recentLimit}
              onChange={(e) => setRecentLimit(parseInt(e.target.value) || 20)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
              min="1"
              max="200"
            />
          </div>

          <div className="tw-flex tw-items-end">
            <button
              onClick={loadRecentChanges}
              className="tw-w-full tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-md hover:tw-bg-blue-700"
              disabled={recentLoading}
            >
              {recentLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="tw-overflow-x-auto">
          <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
            <thead className="tw-bg-gray-100">
              <tr>
                <th className="tw-bg-gray-100 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase">
                  Transaction ID
                </th>
                <th className="tw-bg-gray-100 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase">
                  Timestamp
                </th>
                <th className="tw-bg-gray-100 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase">
                  User ID
                </th>
                <th className="tw-bg-gray-100 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase">
                  User Type
                </th>
                <th className="tw-bg-gray-100 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase">
                  Models Changed
                </th>
                <th className="tw-bg-gray-100 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase">
                  Operations
                </th>
              </tr>
            </thead>
            <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
              {recentChanges.map((change) => (
                <tr key={change.transaction_id} className="hover:tw-bg-gray-50">
                  <td
                    className="tw-px-4 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-cursor-pointer tw-text-blue-600 hover:tw-underline"
                    onClick={() =>
                      handleTransactionIdClick(change.transaction_id)
                    }
                    title="Click to view transaction details"
                  >
                    {change.transaction_id}
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap tw-text-sm">
                    {new Date(change.timestamp).toLocaleString()}
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap tw-text-sm">
                    {change.user_id || "N/A"}
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap tw-text-sm">
                    {change.user_type || "N/A"}
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm">
                    {Object.keys(change.models_changed).join(", ")}
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm">
                    {Object.entries(change.models_changed).map(
                      ([model, ops]) => (
                        <div key={model} className="tw-text-xs">
                          <span className="tw-font-medium">{model}:</span>{" "}
                          {ops.inserts > 0 && (
                            <span className="tw-text-green-600">
                              +{ops.inserts}
                            </span>
                          )}
                          {ops.updates > 0 && (
                            <span className="tw-text-blue-600">
                              {" "}
                              ~{ops.updates}
                            </span>
                          )}
                          {ops.deletes > 0 && (
                            <span className="tw-text-red-600">
                              {" "}
                              -{ops.deletes}
                            </span>
                          )}
                        </div>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {recentChanges.length === 0 && !recentLoading && (
            <div className="tw-text-center tw-py-8 tw-text-gray-500">
              No changes found
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderRecordHistory = () => (
    <div className="tw-space-y-4">
      <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow">
        <h2 className="tw-text-2xl tw-font-bold tw-mb-4">Record History</h2>

        {/* Input Fields */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-6">
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              Model Name
            </label>
            <select
              value={recordModel}
              onChange={(e) => setRecordModel(e.target.value)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
            >
              <option value="PTORequest">PTORequest</option>
              <option value="Log">Log</option>
              <option value="Worker">Worker</option>
              <option value="Plant">Plant</option>
            </select>
          </div>

          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              Record ID
            </label>
            <input
              type="number"
              value={recordId}
              onChange={(e) => setRecordId(e.target.value)}
              placeholder="e.g., 123"
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
            />
          </div>

          <div className="tw-flex tw-items-end">
            <button
              onClick={loadRecordHistory}
              className="tw-w-full tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-md hover:tw-bg-blue-700"
              disabled={recordLoading || !recordId}
            >
              {recordLoading ? "Loading..." : "Load History"}
            </button>
          </div>
        </div>

        {/* Results */}
        {recordHistory && (
          <div>
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3">
              History for {recordHistory.model} #{recordHistory.record_id}
            </h3>
            <div className="tw-space-y-3">
              {recordHistory.history.map((entry, idx) => (
                <div
                  key={idx}
                  className="tw-border tw-rounded-md tw-p-4 tw-bg-gray-50"
                >
                  <div className="tw-flex tw-justify-between tw-items-start tw-mb-2">
                    <div>
                      <span
                        className={`tw-inline-block tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold ${
                          entry.operation === "insert"
                            ? "tw-bg-green-100 tw-text-green-800"
                            : entry.operation === "update"
                            ? "tw-bg-blue-100 tw-text-blue-800"
                            : "tw-bg-red-100 tw-text-red-800"
                        }`}
                      >
                        {entry.operation.toUpperCase()}
                      </span>
                    </div>
                    <div className="tw-text-sm tw-text-gray-600">
                      {entry.timestamp
                        ? new Date(entry.timestamp).toLocaleString()
                        : "N/A"}
                    </div>
                  </div>
                  <div className="tw-text-sm tw-text-gray-600">
                    <span className="tw-text-gray-600">
                      {entry.user_type || "N/A"}:
                    </span>{" "}
                    {entry.user_id || "N/A"} {" - "} {entry.user_name || "N/A"}
                  </div>
                  {/* <div className="tw-text-sm tw-text-gray-600">
                    <span className="tw-text-gray-600">User Type:</span>{" "}
                    {entry.user_type || "N/A"}
                  </div> */}
                  {entry.changes && Object.keys(entry.changes).length > 0 && (
                    <div className="tw-mt-2">
                      <div className="tw-text-sm tw-font-medium tw-mb-1">
                        Changes:
                      </div>
                      <div className="tw-space-y-1">
                        {Object.entries(entry.changes).map(
                          ([field, change]) => (
                            <div key={field} className="tw-text-xs tw-pl-4">
                              <span className="tw-font-medium">{field}:</span>{" "}
                              <span className="tw-text-red-600">
                                {change.old || "null"}
                              </span>{" "}
                              →{" "}
                              <span className="tw-text-green-600">
                                {change.new || "null"}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {recordHistory.history.length === 0 && (
                <div className="tw-text-center tw-py-4 tw-text-gray-500">
                  No history found for this record
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderTransactionDetails = () => (
    <div className="tw-space-y-4">
      <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow">
        <h2 className="tw-text-2xl tw-font-bold tw-mb-4">
          Transaction Details
        </h2>

        {/* Input Fields */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-6">
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              Transaction ID
            </label>
            <input
              type="number"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              placeholder="e.g., 456"
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
            />
          </div>

          <div className="tw-flex tw-items-end">
            <label className="tw-flex tw-items-center">
              <input
                type="checkbox"
                checked={transactionDetailed}
                onChange={(e) => setTransactionDetailed(e.target.checked)}
                className="tw-mr-2"
              />
              <span className="tw-text-sm tw-font-medium">Detailed View</span>
            </label>
          </div>

          <div className="tw-flex tw-items-end">
            <button
              onClick={loadTransactionDetails}
              className="tw-w-full tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-md hover:tw-bg-blue-700"
              disabled={transactionLoading || !transactionId}
            >
              {transactionLoading ? "Loading..." : "Load Transaction"}
            </button>
          </div>
        </div>

        {/* Results */}
        {transactionDetails && (
          <div>
            <div className="tw-mb-4 tw-p-4 tw-bg-gray-50 tw-rounded-md">
              <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4">
                <div>
                  <div className="tw-text-xs tw-text-gray-600">
                    Transaction ID
                  </div>
                  <div className="tw-font-semibold">
                    {transactionDetails.transaction_id}
                  </div>
                </div>
                <div>
                  <div className="tw-text-xs tw-text-gray-600">Timestamp</div>
                  <div className="tw-font-semibold">
                    {"issued_at" in transactionDetails &&
                    transactionDetails.issued_at
                      ? new Date(transactionDetails.issued_at).toLocaleString()
                      : "timestamp" in transactionDetails
                      ? new Date(transactionDetails.timestamp).toLocaleString()
                      : "N/A"}
                  </div>
                </div>
                <div>
                  <div className="tw-text-xs tw-text-gray-600">User ID</div>
                  <div className="tw-font-semibold">
                    {transactionDetails.user_id || "N/A"}
                  </div>
                </div>
                <div>
                  <div className="tw-text-xs tw-text-gray-600">User Type</div>
                  <div className="tw-font-semibold">
                    {transactionDetails.user_type || "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {transactionDetailed && "changes" in transactionDetails ? (
              <div className="tw-space-y-3">
                <h3 className="tw-text-lg tw-font-semibold">
                  Detailed Changes
                </h3>
                {transactionDetails.changes.map((change, idx) => (
                  <div
                    key={idx}
                    className="tw-border tw-rounded-md tw-p-4 tw-bg-gray-50"
                  >
                    <div className="tw-flex tw-justify-between tw-items-start tw-mb-2">
                      <div>
                        <span className="tw-font-semibold">{change.model}</span>
                        {change.id && (
                          <span
                            className="tw-text-blue-600 tw-cursor-pointer hover:tw-underline"
                            onClick={() =>
                              handleRecordIdClick(change.model, change.id!)
                            }
                            title="Click to view record history"
                          >
                            {" "}
                            #{change.id}
                          </span>
                        )}
                      </div>
                      <span
                        className={`tw-inline-block tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold ${
                          change.operation === "insert"
                            ? "tw-bg-green-100 tw-text-green-800"
                            : change.operation === "update"
                            ? "tw-bg-blue-100 tw-text-blue-800"
                            : "tw-bg-red-100 tw-text-red-800"
                        }`}
                      >
                        {change.operation.toUpperCase()}
                      </span>
                    </div>
                    {change.changes && (
                      <div className="tw-mt-2 tw-space-y-1">
                        {Object.entries(change.changes).map(
                          ([field, values]) => (
                            <div key={field} className="tw-text-sm">
                              <span className="tw-font-medium">{field}:</span>{" "}
                              <span className="tw-text-red-600">
                                {values.old || "null"}
                              </span>{" "}
                              →{" "}
                              <span className="tw-text-green-600">
                                {values.new || "null"}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    )}
                    {change.data && (
                      <div className="tw-mt-2 tw-text-xs tw-text-gray-600">
                        <pre className="tw-bg-white tw-p-2 tw-rounded tw-overflow-auto">
                          {JSON.stringify(change.data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              "models_changed" in transactionDetails && (
                <div>
                  <h3 className="tw-text-lg tw-font-semibold tw-mb-2">
                    Models Changed
                  </h3>
                  <div className="tw-space-y-2">
                    {Object.entries(transactionDetails.models_changed).map(
                      ([model, ops]) => (
                        <div
                          key={model}
                          className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-bg-gray-50 tw-rounded-md"
                        >
                          <span className="tw-font-medium">{model}</span>
                          <div className="tw-space-x-3 tw-text-sm">
                            {ops.inserts > 0 && (
                              <span className="tw-text-green-600">
                                +{ops.inserts} inserted
                              </span>
                            )}
                            {ops.updates > 0 && (
                              <span className="tw-text-blue-600">
                                ~{ops.updates} updated
                              </span>
                            )}
                            {ops.deletes > 0 && (
                              <span className="tw-text-red-600">
                                -{ops.deletes} deleted
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderActivity = () => (
    <div className="tw-space-y-4">
      <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow">
        <h2 className="tw-text-2xl tw-font-bold tw-mb-4">User Activity</h2>

        {/* Controls */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4 tw-mb-6">
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              User ID
            </label>
            <input
              type="number"
              value={activityUserId}
              onChange={(e) => setActivityUserId(e.target.value)}
              placeholder="Leave empty for current user"
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
            />
          </div>
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              User Type
            </label>
            <select
              value={activityUserType}
              onChange={(e) => setActivityUserType(e.target.value)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
            >
              <option value="">All Types</option>
              <option value="admin">admin</option>
              <option value="user">user</option>
            </select>
          </div>
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-2">
              Days to Look Back
            </label>
            <select
              value={activityDays}
              onChange={(e) => setActivityDays(parseInt(e.target.value))}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <div className="tw-flex tw-items-end">
            <button
              onClick={loadMyActivity}
              className="tw-w-full tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-md hover:tw-bg-blue-700"
              disabled={activityLoading}
            >
              {activityLoading ? "Loading..." : "Load Activity"}
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        {myActivity && (
          <div className="tw-space-y-6">
            <div className="tw-mb-4 tw-p-3 tw-bg-blue-50 tw-rounded-md tw-border tw-border-blue-200">
              <div className="tw-text-sm tw-font-medium tw-text-blue-900">
                Showing activity for User ID: {myActivity.user_id} | Period:{" "}
                {myActivity.period_days} days
              </div>
            </div>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
              <div className="tw-p-4 tw-bg-blue-50 tw-rounded-lg">
                <div className="tw-text-2xl tw-font-bold tw-text-blue-900">
                  {myActivity.total_transactions}
                </div>
                <div className="tw-text-sm tw-text-blue-700">
                  Total Transactions
                </div>
              </div>
              <div className="tw-p-4 tw-bg-green-50 tw-rounded-lg">
                <div className="tw-text-2xl tw-font-bold tw-text-green-900">
                  {myActivity.operation_counts.inserts}
                </div>
                <div className="tw-text-sm tw-text-green-700">
                  Records Created
                </div>
              </div>
              <div className="tw-p-4 tw-bg-orange-50 tw-rounded-lg">
                <div className="tw-text-2xl tw-font-bold tw-text-orange-900">
                  {myActivity.operation_counts.updates}
                </div>
                <div className="tw-text-sm tw-text-orange-700">
                  Records Updated
                </div>
              </div>
            </div>

            {/* Models Affected */}
            <div>
              <h3 className="tw-text-lg tw-font-semibold tw-mb-3">
                Models Affected
              </h3>
              <div className="tw-space-y-2">
                {Object.entries(myActivity.models_affected).map(
                  ([model, ops]) => (
                    <div
                      key={model}
                      className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-bg-gray-50 tw-rounded-md"
                    >
                      <span className="tw-font-medium">{model}</span>
                      <div className="tw-space-x-3 tw-text-sm">
                        {ops.inserts > 0 && (
                          <span className="tw-text-green-600">
                            +{ops.inserts}
                          </span>
                        )}
                        {ops.updates > 0 && (
                          <span className="tw-text-blue-600">
                            ~{ops.updates}
                          </span>
                        )}
                        {ops.deletes > 0 && (
                          <span className="tw-text-red-600">
                            -{ops.deletes}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overall Stats */}
      {stats && (
        <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow">
          <h2 className="tw-text-2xl tw-font-bold tw-mb-4">
            System-Wide Statistics (Last 30 Days)
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
            <div className="tw-p-4 tw-bg-purple-50 tw-rounded-lg">
              <div className="tw-text-2xl tw-font-bold tw-text-purple-900">
                {stats.total_transactions}
              </div>
              <div className="tw-text-sm tw-text-purple-700">
                Total Transactions
              </div>
            </div>
            <div className="tw-p-4 tw-bg-indigo-50 tw-rounded-lg">
              <div className="tw-text-2xl tw-font-bold tw-text-indigo-900">
                {stats.unique_users}
              </div>
              <div className="tw-text-sm tw-text-indigo-700">Active Users</div>
            </div>
            <div className="tw-p-4 tw-bg-pink-50 tw-rounded-lg">
              <div className="tw-text-2xl tw-font-bold tw-text-pink-900">
                {stats.models_modified.length}
              </div>
              <div className="tw-text-sm tw-text-pink-700">Models Modified</div>
            </div>
            <div className="tw-p-4 tw-bg-teal-50 tw-rounded-lg">
              <div className="tw-text-2xl tw-font-bold tw-text-teal-900">
                {stats.operation_counts.inserts +
                  stats.operation_counts.updates +
                  stats.operation_counts.deletes}
              </div>
              <div className="tw-text-sm tw-text-teal-700">
                Total Operations
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="tw-p-6 tw-max-w-7xl tw-mx-auto">
      <div className="tw-mb-6">
        <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900">
          Audit & Transaction History
        </h1>
        <p className="tw-text-gray-600 tw-mt-2">
          View and analyze database transaction changes and audit logs
        </p>
      </div>

      {/* Tabs */}
      <div className="tw-flex tw-space-x-2 tw-mb-6 tw-border-b">
        <button
          onClick={() => setActiveTab("recent")}
          className={`tw-px-4 tw-py-2 tw-font-medium tw-border-b-2 tw-transition-colors tw-flex tw-items-center tw-gap-2 ${
            activeTab === "recent"
              ? "tw-border-blue-600 tw-text-blue-600"
              : "tw-border-transparent tw-text-gray-600 hover:tw-text-gray-900"
          }`}
        >
          <Clock size={18} />
          Recent Changes
        </button>
        <button
          onClick={() => setActiveTab("transaction")}
          className={`tw-px-4 tw-py-2 tw-font-medium tw-border-b-2 tw-transition-colors tw-flex tw-items-center tw-gap-2 ${
            activeTab === "transaction"
              ? "tw-border-blue-600 tw-text-blue-600"
              : "tw-border-transparent tw-text-gray-600 hover:tw-text-gray-900"
          }`}
        >
          <FileText size={18} />
          Transaction Details
        </button>
        <button
          onClick={() => setActiveTab("record")}
          className={`tw-px-4 tw-py-2 tw-font-medium tw-border-b-2 tw-transition-colors tw-flex tw-items-center tw-gap-2 ${
            activeTab === "record"
              ? "tw-border-blue-600 tw-text-blue-600"
              : "tw-border-transparent tw-text-gray-600 hover:tw-text-gray-900"
          }`}
        >
          <History size={18} />
          Record History
        </button>

        <button
          onClick={() => setActiveTab("activity")}
          className={`tw-px-4 tw-py-2 tw-font-medium tw-border-b-2 tw-transition-colors tw-flex tw-items-center tw-gap-2 ${
            activeTab === "activity"
              ? "tw-border-blue-600 tw-text-blue-600"
              : "tw-border-transparent tw-text-gray-600 hover:tw-text-gray-900"
          }`}
        >
          <Activity size={18} />
          User Activity
        </button>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "recent" && renderRecentChanges()}
        {activeTab === "transaction" && renderTransactionDetails()}
        {activeTab === "record" && renderRecordHistory()}
        {activeTab === "activity" && renderActivity()}
      </div>
    </div>
  );
}
